import {
  Brain,
  CheckCircle2,
  GitBranch,
  Rocket,
  Search,
  Settings,
  Target,
  Terminal,
  TestTube,
} from "lucide-react";
import SlideGuide, {
  type GuideSlide,
  type GuideStep,
} from "@/components/training/SlideGuide";

const STEPS: GuideStep[] = [
  {
    id: "sea-of-demand",
    title: "The Sea of Demand™",
    description: "Validate demand and find a defensible market position.",
    duration: "Week 1",
    whyItMatters:
      "The fastest way to waste an AI build is to automate a problem nobody is actively trying to solve. Start with observed demand, not an imagined feature list.",
    actions: [
      "Find 10 online communities where your ideal customer already gathers.",
      "Observe for one week and document the five most repeated pain points in the customers' own words.",
      "Build an inverted Dream 100: the people, newsletters, podcasts, and communities that already hold your audience's trust.",
      "Conduct 10 customer interviews and save the patterns in icp-interviews.md.",
    ],
    checkpoint:
      "You can name the urgent problem, quote how customers describe it, and show evidence from 10 interviews.",
    icon: Search,
    prompts: [
      {
        label: "Sea of Demand command",
        value:
          "Use sea-of-demand on my PRODUCT_OR_SERVICE for ICP_IDENTITY with PROBLEMS_OR_RISKS",
      },
    ],
    deliverables: [
      "market-research.md",
      "icp-interviews.md with 10 interviews",
    ],
    note: "Do not summarize the market into generic language. Keep the phrases real customers repeat.",
  },
  {
    id: "validated-bet",
    title: "The Validated Bet™",
    description: "Prove interest before you build the full product.",
    duration: "Week 2",
    whyItMatters:
      "A signup is stronger evidence than a compliment. A small landing page turns your market thesis into a measurable bet before you spend weeks building.",
    actions: [
      "Turn the strongest problem and desired outcome into one clear landing-page promise.",
      "Add an email capture and a specific early-access invitation.",
      "Promote the page inside the communities and relationships you mapped in Step 1.",
      "Get at least 10 qualified signups before committing to the full build.",
    ],
    checkpoint:
      "Your landing page is live, the email capture works, and at least 10 qualified people have raised their hands.",
    icon: Target,
    prompts: [{ label: "Validated Bet command", value: "Use validated-bet" }],
    deliverables: ["Landing page", "Email capture system"],
    note: "If the right people do not sign up, revise the promise or audience before adding features.",
  },
  {
    id: "wedge-roadmap",
    title: "The Wedge Roadmap™",
    description: "Prioritize the smallest valuable product that can win.",
    duration: "Week 2",
    whyItMatters:
      "Your wedge is the first job you can solve noticeably better than the alternatives. It keeps the roadmap narrow enough to ship and valuable enough to buy.",
    actions: [
      "Translate interview evidence into Jobs-to-be-Done statements.",
      "Compare the current alternatives customers use, including manual workarounds.",
      "Choose the wedge feature with the strongest mix of urgency, differentiation, and monetization.",
      "Define the first release, explicit non-goals, and success measures in a product requirements document.",
    ],
    checkpoint:
      "roadmap-prd.md names one wedge, one primary user, measurable success criteria, and what is deliberately out of scope.",
    icon: Brain,
    prompts: [{ label: "Wedge Roadmap command", value: "Use wedge-roadmap" }],
    deliverables: ["roadmap-prd.md"],
  },
  {
    id: "fogg-prototype",
    title: "The Fogg Prototype™",
    description: "Design a prototype that makes the desired behavior easier.",
    duration: "Week 3",
    whyItMatters:
      "BJ Fogg's model says behavior happens when motivation, ability, and a prompt converge. A useful prototype removes friction instead of merely looking polished.",
    actions: [
      "Map the shortest user journey from trigger to useful outcome.",
      "Use B = M × A × P to identify motivation gaps, ability barriers, and missing prompts.",
      "Turn the journey into wireframes and complete user stories.",
      "Put the prototype in front of real customers and note where they hesitate, misunderstand, or stop.",
    ],
    checkpoint:
      "ux.md contains the validated journey, wireframes, behavior assumptions, and complete stories for the wedge experience.",
    icon: TestTube,
    prompts: [
      {
        label: "Prototype PRD command",
        value: "Use user-researcher to create a PRD",
      },
    ],
    deliverables: ["ux.md"],
    note: "B = Motivation × Ability × Prompt. Improving ability is often the fastest lever.",
  },
  {
    id: "iteration-loop",
    title: "The Iteration Loop™",
    description: "Turn the validated prototype into working software.",
    duration: "Weeks 3–6",
    whyItMatters:
      "Short build-feedback cycles keep the implementation tied to customer evidence. Each pass should reduce risk, not just add code.",
    actions: [
      "Prototype: feed each complete story from ux.md into v0 and build the visible experience.",
      "Feedback: show the working prototype to people from the waitlist and record what changes their behavior.",
      "Integrate: give the final v0 prototype and approved stories to Claude Code.",
      "Repeat one thin slice at a time until the wedge workflow works end to end.",
    ],
    checkpoint:
      "A customer can complete the wedge workflow in the functional app without a guided demo or manual workaround.",
    icon: Settings,
    deliverables: ["Functional app"],
    note: "Do not treat every suggestion as a requirement. Prefer repeated evidence tied to the wedge outcome.",
  },
  {
    id: "go-to-market",
    title: "The Go-To-Market Launch™",
    description: "Deploy the product and turn validation into revenue.",
    duration: "Weeks 6–8",
    whyItMatters:
      "Shipping is not the finish line. The launch system connects the product to repeatable revenue-generating activity and tells you what to improve next.",
    actions: [
      "Deploy through GitHub and Vercel, connect the custom domain, and verify the live customer journey.",
      "Create a launch kit from the evidence, promise, screenshots, demo, and customer language collected in the previous steps.",
      "Choose the revenue-generating activities you can repeat consistently.",
      "Track signups, activation, conversion, retention, and the qualitative reasons behind each result.",
    ],
    checkpoint:
      "The product is live on its domain, the launch kit is usable, and at least one repeatable revenue-generating activity is running.",
    icon: Rocket,
    prompts: [
      {
        label: "Launch strategy command",
        value:
          "Use launch-strategist to create revenue generating activities",
      },
    ],
    deliverables: ["Live product", "Launch kit", "Revenue activity plan"],
  },
];

const SLIDES: GuideSlide[] = [
  { id: "start", title: "The AI R&D System", kind: "cover", tone: "dark" },
  { id: "outcomes", title: "What you will build", kind: "outcomes", tone: "paper" },
  { id: "setup", title: "Set up the agents repository", kind: "custom", tone: "paper" },
  { id: "roadmap", title: "The six-stage system", kind: "roadmap", tone: "blue" },
  { id: "step-sea-of-demand", title: "Find demand before features", kind: "activity", tone: "paper", stepId: "sea-of-demand", showWhy: true, actionIndexes: [0, 1] },
  { id: "sea-of-demand-map", title: "Map trust and interview the market", kind: "activity", tone: "blue", stepId: "sea-of-demand", actionIndexes: [2, 3], showNote: true, showDeliverables: true },
  { id: "sea-of-demand-prompt", title: "Start the demand research", kind: "prompt", tone: "dark", stepId: "sea-of-demand", promptIndexes: [0], actionIndexes: [0, 1] },
  { id: "sea-of-demand-check", title: "Demand is documented", kind: "checkpoint", tone: "orange", stepId: "sea-of-demand" },
  { id: "step-validated-bet", title: "Turn the thesis into a measurable bet", kind: "activity", tone: "paper", stepId: "validated-bet", showWhy: true, actionIndexes: [0, 1] },
  { id: "validated-bet-prompt", title: "Build and distribute the bet", kind: "prompt", tone: "dark", stepId: "validated-bet", promptIndexes: [0], actionIndexes: [2, 3], showNote: true, showDeliverables: true },
  { id: "validated-bet-check", title: "Ten qualified people raised their hands", kind: "checkpoint", tone: "orange", stepId: "validated-bet" },
  { id: "step-wedge-roadmap", title: "Choose the wedge", kind: "activity", tone: "paper", stepId: "wedge-roadmap", showWhy: true, actionIndexes: [0, 1] },
  { id: "wedge-roadmap-prompt", title: "Write the roadmap PRD", kind: "prompt", tone: "dark", stepId: "wedge-roadmap", promptIndexes: [0], actionIndexes: [2, 3], showDeliverables: true },
  { id: "wedge-roadmap-check", title: "The first release is sharply defined", kind: "checkpoint", tone: "orange", stepId: "wedge-roadmap" },
  { id: "step-fogg-prototype", title: "Design for behavior", kind: "activity", tone: "paper", stepId: "fogg-prototype", showWhy: true, actionIndexes: [0, 1], showNote: true },
  { id: "fogg-prototype-prompt", title: "Turn the journey into testable stories", kind: "prompt", tone: "dark", stepId: "fogg-prototype", promptIndexes: [0], actionIndexes: [2, 3], showDeliverables: true },
  { id: "fogg-prototype-check", title: "The wedge experience is validated", kind: "checkpoint", tone: "orange", stepId: "fogg-prototype" },
  { id: "step-iteration-loop", title: "Prototype, get feedback, integrate", kind: "activity", tone: "paper", stepId: "iteration-loop", showWhy: true, actionIndexes: [0, 1] },
  { id: "iteration-loop-build", title: "Build one thin slice at a time", kind: "activity", tone: "blue", stepId: "iteration-loop", actionIndexes: [2, 3], showNote: true, showDeliverables: true },
  { id: "iteration-loop-check", title: "The core workflow works end to end", kind: "checkpoint", tone: "orange", stepId: "iteration-loop" },
  { id: "step-go-to-market", title: "Deploy the product, not just the code", kind: "activity", tone: "paper", stepId: "go-to-market", showWhy: true, actionIndexes: [0, 1] },
  { id: "go-to-market-prompt", title: "Build the revenue activity plan", kind: "prompt", tone: "dark", stepId: "go-to-market", promptIndexes: [0], actionIndexes: [2, 3], showDeliverables: true },
  { id: "go-to-market-check", title: "The launch system is running", kind: "checkpoint", tone: "orange", stepId: "go-to-market" },
  { id: "finish", title: "Keep shipping", kind: "finish", tone: "dark" },
];

const SetupSlide = () => (
  <div className="mx-auto flex min-h-full w-full max-w-6xl flex-col justify-center py-8 sm:py-12">
    <div className="flex items-center gap-3">
      <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-950 text-white"><GitBranch className="h-5 w-5" /></span>
      <p className="text-xs font-extrabold uppercase tracking-[0.18em] text-orange-700">Before step 1 · setup</p>
    </div>
    <h1 className="mt-5 max-w-5xl font-serif text-4xl font-semibold leading-tight tracking-[-0.04em] sm:text-5xl lg:text-6xl">Put the agent toolkit where Claude Code can use it.</h1>
    <div className="mt-7 grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
      <div className="overflow-hidden rounded-2xl border border-slate-950/10 bg-[#020817] text-white shadow-2xl shadow-slate-950/20">
        <div className="flex items-center gap-3 border-b border-white/10 px-5 py-3 text-sm font-bold text-slate-300"><Terminal className="h-4 w-4 text-orange-300" /> Terminal</div>
        <pre className="overflow-x-auto whitespace-pre-wrap px-5 py-5 font-mono text-sm leading-7 text-slate-100">{`cd ~/.claude\ngit clone git@github.com:technical-leaders/agents.git\ncd agents\nls -la`}</pre>
      </div>
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <p className="text-xs font-extrabold uppercase tracking-[0.16em] text-orange-700">Ready when</p>
        <ul className="mt-4 space-y-4">
          {["Git and Claude Code are installed.", "Your GitHub SSH access works.", "The agents repository is visible inside ~/.claude/agents.", "You have a real product or service idea to test."].map((item) => <li key={item} className="flex items-start gap-3 text-sm font-semibold leading-relaxed text-slate-700"><CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-emerald-500" />{item}</li>)}
        </ul>
      </div>
    </div>
  </div>
);

const AiRdSystem = () => (
  <SlideGuide
    name="The AI R&D System™"
    trackingName="AI R&D System Slide Guide"
    canonicalPath="/ai-rd-system"
    storageKey="ai-rd-system-completed-steps"
    slideStorageKey="ai-rd-system-last-slide"
    seo={{
      title: "AI R&D System: Step-by-Step Slide Guide",
      description: "Build and launch a validated AI product through six guided stages, from market demand and prototyping to deployment and revenue activity.",
      keywords: ["AI R&D system", "AI product development", "market validation", "Claude Code", "AI product launch"],
    }}
    schema={{
      description: "A six-stage AI product research, validation, prototyping, development, and launch system.",
      totalTime: "P8W",
      supplies: ["Claude Code", "GitHub", "Vercel", "Customer interviews"],
    }}
    cover={{
      title: "The AI R&D",
      accent: "System™",
      description: "Move from research to reality with a six-stage, evidence-first system for validating, building, and launching an AI product or service.",
      badges: ["6 stages", "4–8 weeks", "AI-first", "Progress saved"],
      finishLineTitle: "A live product with a launch system",
      finishLineBody: "You will leave with market evidence, a validated wedge, a functional app, and a repeatable plan for turning attention into revenue.",
    }}
    outcomes={{
      eyebrow: "What you will build",
      heading: "Every stage ends in evidence you can carry into the next decision.",
      items: ["Customer language and 10 interview records", "A live validation page with qualified signups", "A focused roadmap PRD", "A behavior-led prototype", "A functional wedge workflow", "A live launch and revenue activity plan"],
      summary: "The system deliberately delays expensive implementation until demand, audience, and the first valuable workflow are concrete.",
    }}
    roadmapHeading="Research the problem. Validate the bet. Build only what earns the next step."
    steps={STEPS}
    slides={SLIDES}
    contentsExtras={[{ slideId: "setup", label: "Setup the toolkit" }]}
    renderCustomSlide={(slide) => slide.id === "setup" ? <SetupSlide /> : null}
    finish={{
      eyebrow: "The loop continues",
      heading: "You shipped the wedge. Now make the system compound.",
      body: "Use live product behavior and customer conversations to decide the next research bet, not a backlog built on assumptions.",
      cards: [
        { eyebrow: "Build better", title: "The AI Builder Playbook", body: "Turn validated stories into a bounded prototype with verification and handoff evidence.", href: "/ai-builder-playbook" },
        { eyebrow: "Prompt better", title: "Fortune 100 Prompt Library", body: "Sign up for free workflows for analysis, strategy, operations, and execution.", href: "/library" },
      ],
    }}
  />
);

export default AiRdSystem;
